import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";

const LoadingContext = createContext({ active: false });

export function LoadingProvider({ children }) {
  const [count, setCount] = useState(0);
  const active = count > 0;

  const start = useCallback(() => setCount(c => c + 1), []);
  const done  = useCallback(() => setCount(c => Math.max(0, c - 1)), []);

  // Expose on window so axios.js (outside React tree) can call it
  const windowRef = useRef(false);
  if (!windowRef.current) {
    windowRef.current = true;
    window.__garmentLoading = { start, done };
  }

  return (
    <LoadingContext.Provider value={{ active }}>
      <GlobalProgressBar active={active} />
      {children}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  return useContext(LoadingContext);
}

// ─── Thin top progress bar ────────────────────────────────────────────────────
//
// State machine:
//   "idle"      → bar not in DOM at all (no flicker on page load, no leftover bar)
//   "running"   → bar grows from 0 to ~95% while request is in flight
//   "finishing" → bar quickly fills to 100% and fades out after request completes
//                 (~250ms) then transitions back to "idle".
//
// Why this is needed: the previous version always rendered the bar, so
// even when no request was active you'd see a thin line lingering at the
// top of the page during every "finishing" animation cycle.
function GlobalProgressBar({ active }) {
  // phase: "idle" | "running" | "finishing"
  const [phase, setPhase] = useState("idle");

  useEffect(() => {
    if (active) {
      setPhase("running");
      return;
    }
    // active flipped false — quick fade then unmount
    if (phase === "running") {
      setPhase("finishing");
      const t = setTimeout(() => setPhase("idle"), 100); // matches 80ms anim + 20ms slack
      return () => clearTimeout(t);
    }
  // We intentionally only react to `active` changes here.  `phase` is
  // updated as a side effect; including it would re-trigger the timer.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (phase === "idle") return null;

  return (
    <>
      <style>{`
        @keyframes garment-progress {
          0%   { width: 0%;   opacity: 1; }
          20%  { width: 30%;  opacity: 1; }
          50%  { width: 65%;  opacity: 1; }
          80%  { width: 85%;  opacity: 1; }
          100% { width: 95%;  opacity: 1; }
        }
        @keyframes garment-done {
          0%   { width: 95%;  opacity: 1; }
          50%  { width: 100%; opacity: 1; }
          100% { width: 100%; opacity: 0; }
        }
        .g-progress-bar {
          position: fixed;
          top: 0; left: 0;
          height: 2.5px;
          z-index: 99999;
          pointer-events: none;
          background: linear-gradient(90deg, #C95210 0%, #C41040 60%, #ff6b35 100%);
          box-shadow: 0 0 8px rgba(201,82,16,0.6);
          border-radius: 0 2px 2px 0;
          transition: none;
        }
        .g-progress-bar.running {
          animation: garment-progress 8s ease-out forwards;
        }
        .g-progress-bar.finishing {
          animation: garment-done 0.08s ease-out forwards;
        }
      `}</style>
      <div className={`g-progress-bar ${phase}`} />
    </>
  );
}